/**
 * VoiceChatConfig — WebRTC voice-chat tuning knobs.
 *
 * Three quality presets cover the common cases:
 *   - lofi   : 12 kbps mono,  DTX+FEC  (party chat, bad network)
 *   - speech : 32 kbps mono,  DTX+FEC  (default — voice)
 *   - hifi   : 64 kbps stereo, no DTX   (streamer / music sharing)
 */

export type VoiceQualityPreset = 'lofi' | 'speech' | 'hifi';

export interface OpusParams {
  /** Target bitrate in bits/sec. */
  bitrate: number;
  /** Discontinuous transmission (silence → comfort noise). Bandwidth win. */
  dtx: boolean;
  /** In-band Forward Error Correction (survives up to ~15% loss). */
  fec: boolean;
  /** Stereo channel count if supported. */
  stereo: boolean;
}

export const VOICE_PRESETS: Record<VoiceQualityPreset, OpusParams> = {
  lofi:   { bitrate: 12_000, dtx: true,  fec: true,  stereo: false },
  speech: { bitrate: 32_000, dtx: true,  fec: true,  stereo: false },
  hifi:   { bitrate: 64_000, dtx: false, fec: true,  stereo: true  },
};

export type VoiceMode = 'alwaysOn' | 'ptt' | 'vadGated';

export interface VoiceChatConfig {
  /** Quality preset (sets Opus params). */
  quality: VoiceQualityPreset;
  /** PTT / always-on / VAD-gated speaking. */
  mode: VoiceMode;
  /**
   * STUN / TURN ICE servers. Default: Google public STUN only.
   * For TURN (needed when symmetric NAT blocks P2P), add your own.
   */
  iceServers: RTCIceServer[];
  /** Enable browser echo cancellation / noise suppression on mic. */
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  /** Mesh cap — above this, refuse new peers (switch to SFU externally). */
  maxPeers: number;
  /** VAD RMS threshold in dBFS. Below this = silent. */
  vadThresholdDb: number;
  /** VAD "hangover" — keep isSpeaking=true for this many ms after crossing down. */
  vadHangoverMs: number;
  /** Enable spatial HRTF panning on remote voices. */
  spatialEnabled: boolean;
  /** Max distance before a remote voice is fully attenuated. */
  spatialMaxDistance: number;
}

export function createVoiceChatConfig(overrides: Partial<VoiceChatConfig> = {}): VoiceChatConfig {
  return {
    quality: overrides.quality ?? 'speech',
    mode: overrides.mode ?? 'vadGated',
    iceServers: overrides.iceServers ?? [{ urls: 'stun:stun.l.google.com:19302' }],
    echoCancellation: overrides.echoCancellation ?? true,
    noiseSuppression: overrides.noiseSuppression ?? true,
    autoGainControl: overrides.autoGainControl ?? true,
    maxPeers: overrides.maxPeers ?? 6,
    vadThresholdDb: overrides.vadThresholdDb ?? -40,
    vadHangoverMs: overrides.vadHangoverMs ?? 250,
    spatialEnabled: overrides.spatialEnabled ?? true,
    spatialMaxDistance: overrides.spatialMaxDistance ?? 50,
  };
}
